import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Library } from "lucide-react";
import { api } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { StarDisplay } from "../components/StarRating";
import { useAuth } from "../context/AuthContext";
import { t, useLang } from "../lib/i18n";

/**
 * `/series/:name` — shows every book in the reader's library that belongs
 * to a given series. Reached by tapping the series badge from any book
 * detail page or library card. Case-insensitive match on `series_name`.
 */
export default function SeriesPage() {
  useLang();
  const { name } = useParams();
  const seriesName = decodeURIComponent(name || "");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user) { navigate(`/login?next=/series/${encodeURIComponent(seriesName)}`); return; }
    setLoading(true);
    api.get("/me/library").then(({ data }) => {
      if (!alive) return;
      const q = seriesName.trim().toLowerCase();
      const filtered = (data || []).filter((row) => (row.book?.series_name || "").trim().toLowerCase() === q);
      // Sort by series index if present, otherwise by title.
      filtered.sort((a, b) => {
        const ai = a.book?.series_index;
        const bi = b.book?.series_index;
        if (ai != null && bi != null) return ai - bi;
        if (ai != null) return -1;
        if (bi != null) return 1;
        return (a.book?.title || "").localeCompare(b.book?.title || "");
      });
      setItems(filtered);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [seriesName, user, navigate]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="series-page">
      <Link to="/library" className="inline-flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A] mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to library
      </Link>
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] inline-flex items-center gap-2">
        <Library className="w-3 h-3" strokeWidth={1.5} /> Series
      </div>
      <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight" data-testid="series-title">{seriesName}</h1>
      <p className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-3" data-testid="series-count">
        {loading ? "Loading…" : `${items.length} ${items.length === 1 ? "book" : "books"} in your library`}
      </p>

      {!loading && items.length === 0 && (
        <div className="mt-16 border border-dashed border-[rgba(10,10,10,0.20)] bg-[#FAF5E6] p-10 text-center">
          <p className="font-heading text-xl text-[#0A0A0A]">No books in this series yet</p>
          <p className="text-[#4A4030] mt-2">Add a book with the series name &ldquo;{seriesName}&rdquo; and it&apos;ll show up here.</p>
        </div>
      )}

      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-4 md:gap-5">
        {items.map(({ book }) => (
          <Link key={book.id} to={`/books/${book.id}`} className="group" data-testid={`series-item-${book.id}`}>
            <BookCover src={book.cover_url} title={book.title} author={book.author} />
            <div className="mt-2.5 space-y-1">
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B7100]">
                #{book.series_index != null ? book.series_index : "?"}
              </div>
              <div className="font-heading text-sm text-[#0A0A0A] leading-tight line-clamp-2 group-hover:text-[#525252] transition-colors">{book.title}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252] line-clamp-1">{book.author}</div>
              {book.avg_rating != null && (
                <div className="flex items-center gap-2 pt-1">
                  <StarDisplay value={book.avg_rating} size={11} />
                  <span className="font-mono text-[9px] text-[#525252]">{Number(book.avg_rating).toFixed(1)}</span>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
